// ============================================
// ADVANCED ADMIN API SERVICE
// Gaming Platform - Enterprise Admin Dashboard
// ============================================

import axios, { AxiosInstance, AxiosError } from 'axios';

// Create API instance
const createApiInstance = (): AxiosInstance => {
  const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
  console.log('[AdminAPI] Creating axios instance with baseURL:', baseURL);

  const api = axios.create({
    baseURL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });


  // Request interceptor
  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('admin_token');
      console.log('[AdminAPI] Making request to:', config.url, 'Token present:', !!token);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor
  api.interceptors.response.use(
    (response) => {
      console.log('[AdminAPI] Response from:', response.config.url, 'Status:', response.status);
      return response;
    },
    (error: AxiosError) => {
      console.log('[AdminAPI] Error from:', error.config?.url, 'Status:', error.response?.status);
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );

  return api;
};

const api = createApiInstance();

// ================== AUTH APIs ==================
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/admin/login', { email, password }),

  logout: () => api.post('/admin/auth/logout'),

  me: () => api.get('/admin/auth/me'),

  refreshToken: () => api.post('/admin/auth/refresh'),

  changePassword: (currentPassword: string, newPassword: string) =>
    api.put('/admin/auth/password', { currentPassword, newPassword }),

  setupTwoFactor: () => api.post('/admin/auth/2fa/setup'),

  verifyTwoFactor: (code: string) => api.post('/admin/auth/2fa/verify', { code }),

  disableTwoFactor: (code: string) => api.post('/admin/auth/2fa/disable', { code }),
};

// ================== DASHBOARD APIs ==================
export const dashboardApi = {
  getStats: (timeRange?: '24h' | '7d' | '30d' | '90d') => {
    console.log('[AdminAPI] Calling getStats with timeRange:', timeRange);
    return api.get('/admin/stats', { params: { timeRange } });
  },


  getRealTimeMetrics: () => api.get('/admin/dashboard/realtime'),

  getChartData: (params: { type: string; period: string }) =>
    api.get('/admin/dashboard/charts', { params }),

  getRevenueAnalytics: (params: { startDate?: string; endDate?: string }) =>
    api.get('/admin/dashboard/revenue', { params }),

  getUserGrowth: (params: { period: string }) =>
    api.get('/admin/dashboard/user-growth', { params }),

  getGameAnalytics: (params: { period: string }) =>
    api.get('/admin/dashboard/games', { params }),

  getActivityFeed: (limit?: number) =>
    api.get('/admin/dashboard/activity', { params: { limit } }),

  getAlerts: () => api.get('/admin/dashboard/alerts'),

  acknowledgeAlert: (alertId: string) =>
    api.put(`/admin/dashboard/alerts/${alertId}/acknowledge`),
};

// ================== USER MANAGEMENT APIs ==================
export const usersApi = {
  // List & Search
  getUsers: (params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    kycStatus?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    dateFrom?: string;
    dateTo?: string;
  }) => api.get('/admin/users', { params }),

  // Single User
  getUser: (userId: string) => api.get(`/admin/users/${userId}`),

  getUserStats: (userId: string) => api.get(`/admin/users/${userId}/stats`),

  getUserTransactions: (userId: string, params?: {
    page?: number;
    limit?: number;
    type?: string;
    status?: string;
  }) => api.get(`/admin/users/${userId}/transactions`, { params }),

  getUserGames: (userId: string, params?: {
    page?: number;
    limit?: number;
    status?: string;
    gameType?: string;
  }) => api.get(`/admin/users/${userId}/games`, { params }),

  getUserActivity: (userId: string, params?: {
    page?: number;
    limit?: number;
  }) => api.get(`/admin/users/${userId}/activity`, { params }),

  getUserReferrals: (userId: string) => api.get(`/admin/users/${userId}/referrals`),

  // User Actions
  updateUser: (userId: string, data: Partial<any>) =>
    api.put(`/admin/users/${userId}`, data),

  banUser: (userId: string, reason: string) =>
    api.put(`/admin/users/${userId}/ban`, { reason }),

  unbanUser: (userId: string) =>
    api.put(`/admin/users/${userId}/unban`),

  adjustBalance: (userId: string, data: {
    amount: number;
    type: 'credit' | 'debit';
    notes: string;
  }) => api.put(`/admin/users/${userId}/balance`, data),

  resetPassword: (userId: string) =>
    api.post(`/admin/users/${userId}/reset-password`),

  sendNotification: (userId: string, data: {
    title: string;
    message: string;
    type: 'push' | 'sms' | 'email';
  }) => api.post(`/admin/users/${userId}/notification`, data),

  // Export
  exportUsers: (params?: any) =>
    api.get('/admin/users/export', { params, responseType: 'blob' }),

  // Bulk Actions
  bulkBan: (userIds: string[], reason: string) =>
    api.post('/admin/users/bulk/ban', { userIds, reason }),

  bulkNotify: (userIds: string[], notification: { title: string; message: string }) =>
    api.post('/admin/users/bulk/notify', { userIds, notification }),
};

// ================== KYC MANAGEMENT APIs ==================
export const kycApi = {
  // List & Filter
  getApplications: (params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    dateFrom?: string;
    dateTo?: string;
  }) => api.get('/admin/kyc', { params }),

  // Single Application
  getApplication: (kycId: string) => api.get(`/admin/kyc/${kycId}`),

  // Stats
  getStats: () => api.get('/admin/kyc/stats'),

  // Actions
  startReview: (kycId: string) =>
    api.put(`/admin/kyc/${kycId}/review`),

  approveKYC: (kycId: string, notes?: string) =>
    api.put(`/admin/kyc/${kycId}/approve`, { notes }),

  rejectKYC: (kycId: string, data: {
    reason: string;
    category: string;
  }) => api.put(`/admin/kyc/${kycId}/reject`, data),

  requestRevision: (kycId: string, notes: string) =>
    api.put(`/admin/kyc/${kycId}/revision`, { notes }),

  addNote: (kycId: string, note: string) =>
    api.post(`/admin/kyc/${kycId}/notes`, { note }),

  // Fraud Detection
  runFraudCheck: (kycId: string) =>
    api.post(`/admin/kyc/${kycId}/fraud-check`),

  checkDuplicate: (kycId: string) =>
    api.post(`/admin/kyc/${kycId}/duplicate-check`),
};

// ================== TRANSACTION APIs ==================
export const transactionsApi = {
  // List & Filter
  getTransactions: (params: {
    page?: number;
    limit?: number;
    search?: string;
    type?: string;
    status?: string;
    userId?: string;
    minAmount?: number;
    maxAmount?: number;
    dateFrom?: string;
    dateTo?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => api.get('/admin/transactions', { params }),

  // Single Transaction
  getTransaction: (transactionId: string) =>
    api.get(`/admin/transactions/${transactionId}`),

  // Stats
  getStats: () => api.get('/admin/transactions/stats'),

  getDailyReport: (date: string) =>
    api.get('/admin/transactions/daily-report', { params: { date } }),

  // Export
  exportTransactions: (params?: any) =>
    api.get('/admin/transactions/export', { params, responseType: 'blob' }),
};

// ================== DEPOSIT MANAGEMENT APIs ==================
export const depositsApi = {
  // List Pending Deposits (Manual Verification)
  getPendingDeposits: (params?: {
    page?: number;
    limit?: number;
    search?: string;
  }) => api.get('/admin/deposits/pending', { params }),

  // Get All Deposits
  getDeposits: (params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
  }) => api.get('/admin/deposits', { params }),

  // Single Deposit
  getDeposit: (depositId: string) =>
    api.get(`/admin/deposits/${depositId}`),

  // Verify Deposit (Manual)
  verifyDeposit: (depositId: string, data: {
    verified: boolean;
    notes?: string;
    rejectionReason?: string;
  }) => api.put(`/admin/deposits/${depositId}/verify`, data),

  // Flag for Review
  flagDeposit: (depositId: string, reason: string) =>
    api.put(`/admin/deposits/${depositId}/flag`, { reason }),
};

// ================== WITHDRAWAL MANAGEMENT APIs ==================
export const withdrawalsApi = {
  // List Pending Withdrawals
  getPendingWithdrawals: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => api.get('/admin/withdrawals/pending', { params }),

  // Get All Withdrawals
  getWithdrawals: (params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    method?: string;
    dateFrom?: string;
    dateTo?: string;
  }) => api.get('/admin/withdrawals', { params }),

  // Single Withdrawal
  getWithdrawal: (withdrawalId: string) =>
    api.get(`/admin/withdrawals/${withdrawalId}`),

  // Process Withdrawal
  approveWithdrawal: (withdrawalId: string, notes?: string) =>
    api.put(`/admin/withdrawals/${withdrawalId}/approve`, { notes }),

  rejectWithdrawal: (withdrawalId: string, reason: string) =>
    api.put(`/admin/withdrawals/${withdrawalId}/reject`, { reason }),

  markAsProcessing: (withdrawalId: string) =>
    api.put(`/admin/withdrawals/${withdrawalId}/processing`),

  markAsCompleted: (withdrawalId: string, data: {
    transactionRef?: string;
    notes?: string;
  }) => api.put(`/admin/withdrawals/${withdrawalId}/complete`, data),

  // Bulk Actions
  bulkApprove: (withdrawalIds: string[]) =>
    api.post('/admin/withdrawals/bulk/approve', { withdrawalIds }),

  bulkReject: (withdrawalIds: string[], reason: string) =>
    api.post('/admin/withdrawals/bulk/reject', { withdrawalIds, reason }),

  // Export
  exportWithdrawals: (params?: any) =>
    api.get('/admin/withdrawals/export', { params, responseType: 'blob' }),
};

// ================== QR CODE MANAGEMENT APIs ==================
export const qrCodesApi = {
  // List QR Codes
  getQRCodes: (params?: {
    page?: number;
    limit?: number;
    isActive?: boolean;
  }) => api.get('/admin/qr-codes', { params }),

  // Single QR Code
  getQRCode: (qrCodeId: string) =>
    api.get(`/admin/qr-codes/${qrCodeId}`),

  // Create QR Code
  createQRCode: (data: {
    name: string;
    upiId: string;
    qrImage: File;
    priority?: number;
    dailyLimit?: number;
    monthlyLimit?: number;
  }) => {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined) {
        formData.append(key, value as any);
      }
    });
    return api.post('/admin/qr-codes', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Update QR Code
  updateQRCode: (qrCodeId: string, data: {
    name?: string;
    isActive?: boolean;
    priority?: number;
    dailyLimit?: number;
    monthlyLimit?: number;
  }) => api.put(`/admin/qr-codes/${qrCodeId}`, data),

  // Delete QR Code
  deleteQRCode: (qrCodeId: string) =>
    api.delete(`/admin/qr-codes/${qrCodeId}`),

  // Get Active QR Code
  getActiveQRCode: () =>
    api.get('/admin/qr-codes/active'),
};

// ================== GAME MANAGEMENT APIs ==================
export const gamesApi = {
  // List Games
  getGames: (params: {
    page?: number;
    limit?: number;
    search?: string;
    gameType?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
  }) => api.get('/admin/games', { params }),

  // Single Game
  getGame: (gameId: string) =>
    api.get(`/admin/games/${gameId}`),

  // Stats
  getStats: () => api.get('/admin/games/stats'),

  // Live Games
  getLiveGames: () => api.get('/admin/games/live'),

  // Game Actions
  cancelGame: (gameId: string, reason: string) =>
    api.put(`/admin/games/${gameId}/cancel`, { reason }),

  setWinner: (gameId: string, winnerId: string, notes?: string) =>
    api.put(`/admin/games/${gameId}/winner`, { winnerId, notes }),

  refundGame: (gameId: string, reason: string) =>
    api.put(`/admin/games/${gameId}/refund`, { reason }),

  // Result Verification
  getResultsPending: (params?: {
    page?: number;
    limit?: number;
  }) => api.get('/admin/games/results/pending', { params }),

  verifyResult: (gameId: string, data: {
    verified: boolean;
    winnerId?: string;
    notes?: string;
  }) => api.put(`/admin/games/${gameId}/result/verify`, data),

  // Disputes
  getDisputes: (params?: {
    page?: number;
    limit?: number;
    status?: string;
  }) => api.get('/admin/games/disputes', { params }),

  resolveDispute: (disputeId: string, data: {
    resolution: string;
    winnerId?: string;
    refund?: boolean;
  }) => api.put(`/admin/games/disputes/${disputeId}/resolve`, data),

  // Game Result Verification (Screenshot-based)
  getPendingVerification: (params?: {
    page?: number;
    limit?: number;
  }) => api.get('/admin/games/pending-verification', { params }),

  verifyGameResult: (roomId: string, data: {
    action: 'approve' | 'reject';
    notes?: string;
    winnerIds?: string[];
  }) => api.put(`/admin/games/${roomId}/verify`, data),
};

// ================== PAYMENT BALANCE APIs ==================
export const paymentBalanceApi = {
  // Get Balance Overview
  getBalance: () => api.get('/admin/payments/balance'),

  // Get Reconciliation Report
  getReconciliation: (date?: string) =>
    api.get('/admin/payments/reconciliation', { params: { date } }),

  // Get Payment Analytics
  getAnalytics: (params: { startDate: string; endDate: string }) =>
    api.get('/admin/payments/analytics', { params }),

  // Get Payment Alerts
  getAlerts: (params?: {
    acknowledged?: boolean;
    severity?: string;
  }) => api.get('/admin/payments/alerts', { params }),

  // Acknowledge Alert
  acknowledgeAlert: (alertId: string, notes?: string) =>
    api.put(`/admin/payments/alerts/${alertId}/acknowledge`, { notes }),

  // Manual Balance Adjustment
  adjustBalance: (data: {
    type: 'credit' | 'debit';
    amount: number;
    reason: string;
    notes: string;
  }) => api.post('/admin/payments/adjust', data),

  // Run Manual Reconciliation
  runReconciliation: () =>
    api.post('/admin/payments/reconciliation/run'),
};

// ================== ADMIN USER MANAGEMENT APIs ==================
export const adminUsersApi = {
  // List Admin Users
  getAdmins: (params?: {
    page?: number;
    limit?: number;
    role?: string;
    isActive?: boolean;
  }) => api.get('/admin/admins', { params }),

  // Single Admin
  getAdmin: (adminId: string) =>
    api.get(`/admin/admins/${adminId}`),

  // Create Admin
  createAdmin: (data: {
    email: string;
    password: string;
    name: string;
    role: string;
    permissions: string[];
  }) => api.post('/admin/admins', data),

  // Update Admin
  updateAdmin: (adminId: string, data: {
    name?: string;
    role?: string;
    permissions?: string[];
    isActive?: boolean;
  }) => api.put(`/admin/admins/${adminId}`, data),

  // Delete Admin
  deleteAdmin: (adminId: string) =>
    api.delete(`/admin/admins/${adminId}`),

  // Reset Password
  resetAdminPassword: (adminId: string) =>
    api.post(`/admin/admins/${adminId}/reset-password`),

  // Get Permissions List
  getPermissions: () =>
    api.get('/admin/admins/permissions'),
};

// ================== AUDIT LOG APIs ==================
export const auditApi = {
  // List Audit Logs
  getLogs: (params: {
    page?: number;
    limit?: number;
    adminId?: string;
    action?: string;
    module?: string;
    dateFrom?: string;
    dateTo?: string;
  }) => api.get('/admin/audit', { params }),

  // Export Audit Logs
  exportLogs: (params?: any) =>
    api.get('/admin/audit/export', { params, responseType: 'blob' }),
};

// ================== NOTIFICATION APIs ==================
export const notificationsApi = {
  // List Notifications
  getNotifications: (params?: {
    page?: number;
    limit?: number;
    type?: string;
    status?: string;
  }) => api.get('/admin/notifications', { params }),

  // Send Notification
  sendNotification: (data: {
    type: 'push' | 'sms' | 'email';
    title: string;
    message: string;
    targetType: 'all' | 'segment' | 'individual';
    targetUsers?: string[];
    targetSegment?: any;
    scheduledAt?: string;
  }) => api.post('/admin/notifications/send', data),

  // Templates
  getTemplates: () => api.get('/admin/notifications/templates'),

  createTemplate: (data: {
    name: string;
    type: string;
    title: string;
    message: string;
  }) => api.post('/admin/notifications/templates', data),

  updateTemplate: (templateId: string, data: any) =>
    api.put(`/admin/notifications/templates/${templateId}`, data),

  deleteTemplate: (templateId: string) =>
    api.delete(`/admin/notifications/templates/${templateId}`),

  // Segment Estimation
  estimateSegment: (filters: any) =>
    api.post('/admin/notifications/segment/estimate', { filters }),
};

// ================== SUPPORT APIs ==================
export const supportApi = {
  // List Tickets
  getTickets: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    priority?: string;
    category?: string;
    assignedTo?: string;
    search?: string;
  }) => api.get('/admin/support/tickets', { params }),

  // Single Ticket
  getTicket: (ticketId: string) =>
    api.get(`/admin/support/tickets/${ticketId}`),

  // Stats
  getStats: () => api.get('/admin/support/stats'),

  // Update Ticket
  updateTicket: (ticketId: string, data: {
    status?: string;
    priority?: string;
    assignedTo?: string;
  }) => api.put(`/admin/support/tickets/${ticketId}`, data),

  // Add Reply
  addReply: (ticketId: string, message: string, attachments?: File[]) => {
    const formData = new FormData();
    formData.append('message', message);
    attachments?.forEach((file) => formData.append('attachments', file));
    return api.post(`/admin/support/tickets/${ticketId}/reply`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Resolve Ticket
  resolveTicket: (ticketId: string, resolution: string) =>
    api.put(`/admin/support/tickets/${ticketId}/resolve`, { resolution }),

  // Close Ticket
  closeTicket: (ticketId: string) =>
    api.put(`/admin/support/tickets/${ticketId}/close`),
};

// ================== PROMOTIONS APIs ==================
export const promotionsApi = {
  // List Promotions
  getPromotions: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    type?: string;
    search?: string;
  }) => api.get('/admin/promotions', { params }),

  // Stats
  getStats: () => api.get('/admin/promotions/stats'),

  // Create Promotion
  createPromotion: (data: {
    title: string;
    description: string;
    type: string;
    startDate: string;
    endDate: string;
    budget: number;
  }) => api.post('/admin/promotions', data),

  // Update Promotion
  updatePromotion: (id: string, data: {
    title?: string;
    description?: string;
    type?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    budget?: number;
  }) => api.put(`/admin/promotions/${id}`, data),

  // Delete Promotion
  deletePromotion: (id: string) => api.delete(`/admin/promotions/${id}`),
};

// ================== BONUSES APIs ==================
export const bonusesApi = {
  // List Bonuses
  getBonuses: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    type?: string;
    search?: string;
  }) => api.get('/admin/bonuses', { params }),

  // Stats
  getStats: () => api.get('/admin/bonuses/stats'),

  // Create Bonus
  createBonus: (data: {
    name: string;
    description?: string;
    type: string;
    amount: number;
    conditions?: string;
    maxClaims?: number;
    minDeposit?: number;
    validFrom?: string;
    validUntil?: string;
  }) => api.post('/admin/bonuses', data),

  // Update Bonus
  updateBonus: (id: string, data: {
    name?: string;
    description?: string;
    type?: string;
    status?: string;
    amount?: number;
    conditions?: string;
    maxClaims?: number;
    minDeposit?: number;
    validFrom?: string;
    validUntil?: string;
  }) => api.put(`/admin/bonuses/${id}`, data),

  // Delete Bonus
  deleteBonus: (id: string) => api.delete(`/admin/bonuses/${id}`),
};

// ================== REFERRAL APIs ==================
export const referralsApi = {
  // List Referrals
  getReferrals: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
  }) => api.get('/admin/referrals', { params }),

  // Stats
  getStats: () => api.get('/admin/referrals/stats'),

  // Top Referrers
  getTopReferrers: (limit?: number) =>
    api.get('/admin/referrals/top', { params: { limit } }),

  // Settings
  getSettings: () => api.get('/admin/referrals/settings'),

  updateSettings: (data: {
    referrerBonus: number;
    referredBonus: number;
    requirement: number;
    isActive: boolean;
  }) => api.put('/admin/referrals/settings', data),
};

// ================== SETTINGS APIs ==================
export const settingsApi = {
  // Get Settings
  getSettings: () => api.get('/admin/settings'),

  // Update Settings
  updateSettings: (data: Partial<any>) =>
    api.put('/admin/settings', data),

  // System Health
  getSystemHealth: () => api.get('/admin/system/health'),

  // Maintenance Mode
  setMaintenanceMode: (enabled: boolean, message?: string) =>
    api.put('/admin/settings/maintenance', { enabled, message }),

  // Game Settings
  getGameSettings: () => api.get('/admin/settings/games'),

  updateGameSettings: (data: any) =>
    api.put('/admin/settings/games', data),

  // Payment Settings
  getPaymentSettings: () => api.get('/admin/settings/payments'),

  updatePaymentSettings: (data: any) =>
    api.put('/admin/settings/payments', data),
};

// ================== REPORTS APIs ==================
export const reportsApi = {
  // Revenue Report
  getRevenueReport: (params: {
    startDate: string;
    endDate: string;
    groupBy?: 'day' | 'week' | 'month';
  }) => api.get('/admin/reports/revenue', { params }),

  // User Report
  getUserReport: (params: {
    startDate: string;
    endDate: string;
    metrics?: string[];
  }) => api.get('/admin/reports/users', { params }),

  // Game Report
  getGameReport: (params: {
    startDate: string;
    endDate: string;
    gameType?: string;
  }) => api.get('/admin/reports/games', { params }),

  // Financial Report
  getFinancialReport: (params: {
    startDate: string;
    endDate: string;
  }) => api.get('/admin/reports/financial', { params }),

  // Generate Custom Report
  generateReport: (data: {
    type: string;
    params: any;
    format: 'csv' | 'xlsx' | 'pdf';
  }) => api.post('/admin/reports/generate', data, { responseType: 'blob' }),

  // Scheduled Reports
  getScheduledReports: () => api.get('/admin/reports/scheduled'),

  createScheduledReport: (data: {
    name: string;
    type: string;
    params: any;
    schedule: string;
    recipients: string[];
  }) => api.post('/admin/reports/scheduled', data),

  deleteScheduledReport: (reportId: string) =>
    api.delete(`/admin/reports/scheduled/${reportId}`),
};

// ================== LEADERBOARD APIs ==================
export const leaderboardApi = {
  // Get Leaderboard
  getLeaderboard: (params?: {
    type?: 'wins' | 'winnings' | 'games';
    period?: 'daily' | 'weekly' | 'monthly' | 'all_time';
    limit?: number;
  }) => api.get('/admin/leaderboard', { params }),

  // Top Winners
  getTopWinners: (period?: string, limit?: number) =>
    api.get('/admin/leaderboard/winners', { params: { period, limit } }),

  // Top Referrers
  getTopReferrers: (limit?: number) =>
    api.get('/admin/leaderboard/referrers', { params: { limit } }),
};

// Export default API instance for custom calls
export default api;
